class ApiService {
  constructor() {
    // Use environment variable or detect local development
    if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
      this.baseUrl = 'https://localhost:7080/api'; // Local .NET API
    } else {
      this.baseUrl = process.env.REACT_APP_API_URL || 'https://xero-payment-api-cmb-c0epbxffhccgdsbs.canadacentral-01.azurewebsites.net/api';
    }
    console.log('🔗 API Base URL:', this.baseUrl);
    console.log('🌍 Environment:', process.env.NODE_ENV);
    console.log('🏠 Hostname:', window.location.hostname);
  }

  // Helper method for API calls
  async apiCall(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Get token for authentication
    const token = localStorage.getItem('token');
    console.log('🔐 Auth token for API call:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers
      },
      ...options
    };

    console.log(`📡 API Call: ${config.method || 'GET'} ${endpoint}`);
    console.log('📋 Headers:', config.headers);

    try {
      const response = await fetch(url, config);
      console.log(`📊 Response: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        // First get the raw text to see what the server actually returned
        const rawText = await response.text();
        console.error(`🔍 Raw response text:`, rawText);
        
        let errorData;
        try {
          errorData = JSON.parse(rawText);
        } catch (jsonError) {
          console.error(`❌ Failed to parse JSON response:`, jsonError);
          // If it's not JSON, use the raw text as the error message
          errorData = { error: rawText || `HTTP ${response.status}: ${response.statusText}` };
        }
        
        console.error(`❌ API Error: ${response.status}`, errorData);
        console.error(`🔍 Parsed error data structure:`, JSON.stringify(errorData, null, 2));
        
        // Create a more informative error with status code
        // Try multiple common error message fields (ASP.NET Core returns different formats)
        const errorMessage = errorData.error || 
                            errorData.message || 
                            errorData.title || 
                            errorData.detail ||
                            errorData.value ||  // ASP.NET Core BadRequest format
                            (typeof errorData === 'string' ? errorData : '') ||
                            `HTTP ${response.status}: ${response.statusText}`;
        const detailedError = new Error(errorMessage);
        detailedError.status = response.status;
        detailedError.statusText = response.statusText;
        detailedError.endpoint = endpoint;
        detailedError.url = url;
        detailedError.errorData = errorData;
        
        throw detailedError;
      }

      // Handle 204 No Content responses (successful but no body)
      if (response.status === 204) {
        console.log('✅ API Success: No content (204)');
        return null;
      }

      // Check if response has content to parse
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json();
        console.log('✅ API Success:', result);
        return result;
      } else {
        console.log('✅ API Success: Non-JSON response');
        return null;
      }
    } catch (error) {
      // Handle network errors specifically
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error(`🔗 Network error for ${endpoint}:`, error);
        const networkError = new Error(`Network error: Unable to connect to ${this.baseUrl}. Please check if the API server is running.`);
        networkError.originalError = error;
        networkError.endpoint = endpoint;
        networkError.isNetworkError = true;
        throw networkError;
      }
      
      console.error(`💥 API call failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Generic HTTP methods
  async get(endpoint) {
    return await this.apiCall(endpoint);
  }

  async post(endpoint, data) {
    return await this.apiCall(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async put(endpoint, data) {
    return await this.apiCall(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async delete(endpoint) {
    return await this.apiCall(endpoint, {
      method: 'DELETE'
    });
  }

  // Get Xero authorization URL
  async getAuthorizationUrl() {
    return await this.apiCall('/xero/auth-url');
  }

  // Exchange authorization code for token
  async exchangeCodeForToken(code, state) {
    return await this.apiCall('/xero/exchange-token', {
      method: 'POST',
      body: JSON.stringify({ code, state })
    });
  }

  // Get connection status
  async getConnectionStatus() {
    return await this.apiCall('/xero/connection-status');
  }

  // Disconnect from Xero
  async disconnectFromXero() {
    return await this.apiCall('/xero/disconnect', { method: 'POST' });
  }

  // Get Xero financial reports
  async getXeroProfitAndLoss(fromDate = null, toDate = null) {
    const params = new URLSearchParams();
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const url = `/xero/reports/profit-and-loss${params.toString() ? '?' + params.toString() : ''}`;
    return await this.apiCall(url);
  }

  async getXeroBalanceSheet(asOfDate = null) {
    const params = new URLSearchParams();
    if (asOfDate) params.append('asOfDate', asOfDate);
    
    const url = `/xero/reports/balance-sheet${params.toString() ? '?' + params.toString() : ''}`;
    return await this.apiCall(url);
  }

  async getXeroTrialBalance(asOfDate = null) {
    const params = new URLSearchParams();
    if (asOfDate) params.append('asOfDate', asOfDate);
    
    const url = `/xero/reports/trial-balance${params.toString() ? '?' + params.toString() : ''}`;
    return await this.apiCall(url);
  }

  async getXeroCashFlowAging() {
    return await this.apiCall('/xero/reports/cash-flow-aging');
  }

  // Clear server-side cache for financial reports
  async clearXeroReportsCache() {
    return await this.apiCall('/xero/reports/clear-cache', {
      method: 'POST'
    });
  }

  // Fetch a persisted report snapshot (no Xero connection required)
  async getReportSnapshot(reportType) {
    return await this.apiCall(`/xero/reports/snapshot/${reportType}`);
  }

  // Fetch ages of all available snapshots
  async getSnapshotAges() {
    return await this.apiCall('/xero/reports/snapshot-ages');
  }

  // Get authorization URL for Xero OAuth
  async getAuthorizationUrl() {
    return await this.apiCall('/xero/auth-url');
  }

  // Exchange authorization code for token
  async exchangeCodeForToken(code, state) {
    return await this.apiCall('/xero/exchange-token', {
      method: 'POST',
      body: JSON.stringify({ code, state })
    });
  }

  // Get bank accounts
  async getBankAccounts() {
    return await this.apiCall('/xero/bank-accounts');
  }

  // Parse PDF remittance file
  async parsePdf(formData) {
    const url = `${this.baseUrl}/xero/parse-pdf`;
    const config = {
      method: 'POST',
      body: formData // Don't set Content-Type, let browser set it with boundary
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API call failed for parse-pdf:`, error);
      throw error;
    }
  }

  // Get contacts from Xero (uses cached contacts with fallback)
  async getContacts() {
    return await this.apiCall('/xero/contacts');
  }

  // Sync contacts from Xero to local cache
  async syncContacts() {
    return await this.apiCall('/xero/sync-contacts', {
      method: 'POST'
    });
  }

  // Get invoices from Xero
  async getInvoices(contactId = null) {
    const params = contactId ? `?contactId=${contactId}` : '';
    return await this.apiCall(`/xero/invoices${params}`);
  }

  // Get single invoice details by ID
  async getInvoiceDetails(invoiceId) {
    return await this.apiCall(`/xero/invoices/${invoiceId}`);
  }

  // Find invoices by numbers
  async findInvoicesByNumbers(invoiceNumbers, contactId = null) {
    return await this.apiCall('/xero/find-invoices', {
      method: 'POST',
      body: JSON.stringify({ invoiceNumbers, contactId })
    });
  }

  // Get all invoices (bulk load for efficient matching)
  async getAllInvoices() {
    return await this.apiCall('/xero/all-invoices');
  }

  // Create invoice
  async createInvoice(invoiceData) {
    return await this.apiCall('/xero/create-invoice', {
      method: 'POST',
      body: JSON.stringify(invoiceData)
    });
  }

  // Update invoice
  async updateInvoice(invoiceId, updateData) {
    return await this.apiCall(`/xero/invoices/${invoiceId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }

  // Create bill
  async createBill(billData) {
    return await this.apiCall('/xero/create-bill', {
      method: 'POST',
      body: JSON.stringify(billData)
    });
  }

  // Create multiple invoices and then payment (if requested)
  async createInvoicesAndPayment(requestData) {
    return await this.apiCall('/xero/create-invoices-and-payment', {
      method: 'POST',
      body: JSON.stringify(requestData)
    });
  }

  // Create multiple invoices and then payment - returns formatted text report
  async createInvoicesAndPaymentReport(requestData) {
    const url = `${this.baseUrl}/xero/create-invoices-and-payment-report`;
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Return plain text instead of JSON
      return await response.text();
    } catch (error) {
      console.error(`API call failed for create-invoices-and-payment-report:`, error);
      throw error;
    }
  }

  // Create single payment
  async createPayment(paymentData) {
    return await this.apiCall('/xero/create-payment', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  // Create single payment with allocations
  async createSinglePayment(paymentData) {
    return await this.apiCall('/xero/create-single-payment', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  // Preview payment JSON without posting to Xero
  async previewPayment(paymentData) {
    return await this.apiCall('/xero/preview-payment', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  // Create overpayment with allocations (RECEIVE-OVERPAYMENT method)
  async createOverpayment(paymentData) {
    return await this.apiCall('/xero/create-overpayment', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  // Preview overpayment JSON without posting to Xero
  async previewOverpayment(paymentData) {
    return await this.apiCall('/xero/preview-overpayment', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  // Create multiple payments (deprecated - use createSinglePayment or createOverpayment instead)
  async createMultiplePayments(paymentsData) {
    return await this.apiCall('/xero/create-multiple-payments', {
      method: 'POST',
      body: JSON.stringify(paymentsData)
    });
  }

  // ===============================
  // NEW SDK-BASED METHODS (Recommended)
  // ===============================

  // Get Xero authorization URL (SDK)
  async getSDKAuthorizationUrl() {
    return await this.apiCall('/xero/sdk/auth-url');
  }

  // Exchange authorization code for token (SDK)
  async exchangeSDKCodeForToken(code, state) {
    return await this.apiCall('/xero/sdk/exchange-token', {
      method: 'POST',
      body: JSON.stringify({ code, state })
    });
  }

  // Get connection status (SDK)
  async getSDKConnectionStatus() {
    return await this.apiCall('/xero/sdk/connection-status');
  }

  // Get contacts from Xero (SDK)
  async getSDKContacts() {
    return await this.apiCall('/xero/sdk/contacts');
  }

  // Find invoices by numbers (SDK)
  async findSDKInvoicesByNumbers(invoiceNumbers, contactId = null) {
    return await this.apiCall('/xero/sdk/find-invoices', {
      method: 'POST',
      body: JSON.stringify({ invoiceNumbers, contactId })
    });
  }

  // Create overpayment with allocations (SDK)
  async createSDKOverpayment(paymentData) {
    return await this.apiCall('/xero/sdk/create-overpayment', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  // Preview overpayment (SDK)
  async previewSDKOverpayment(paymentData) {
    return await this.apiCall('/xero/sdk/preview-overpayment', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }

  // Work Order Management
  async getWorkOrders(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = `/workorder${queryString ? '?' + queryString : ''}`;
    
    // Get token for authentication
    const token = localStorage.getItem('token');
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    };

    console.log(`📡 API Call: GET ${url}`);
    
    try {
      const response = await fetch(`${this.baseUrl}${url}`, config);
      console.log(`📊 Response: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        const rawText = await response.text();
        console.error(`🔍 Raw response text:`, rawText);
        
        let errorData;
        try {
          errorData = JSON.parse(rawText);
        } catch (jsonError) {
          errorData = { error: rawText || `HTTP ${response.status}: ${response.statusText}` };
        }
        
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Extract pagination headers
      const totalCount = parseInt(response.headers.get('X-Total-Count') || '0');
      const page = parseInt(response.headers.get('X-Page') || '1');
      const pageSize = parseInt(response.headers.get('X-Page-Size') || '20');
      
      console.log('📄 Pagination headers:', { totalCount, page, pageSize });
      
      // Return data with pagination info
      return {
        data: data,
        pagination: {
          totalCount,
          page,
          pageSize,
          totalPages: Math.ceil(totalCount / pageSize)
        }
      };
    } catch (error) {
      console.error('❌ API call failed:', error);
      throw error;
    }
  }

  async getWorkOrder(id) {
    return await this.apiCall(`/workorder/${id}`);
  }

  async createWorkOrder(workOrderData) {
    return await this.apiCall('/workorder', {
      method: 'POST',
      body: JSON.stringify(workOrderData)
    });
  }

  async updateWorkOrder(id, workOrderData) {
    return await this.apiCall(`/workorder/${id}`, {
      method: 'PUT',
      body: JSON.stringify(workOrderData)
    });
  }

  async deleteWorkOrder(id) {
    return await this.apiCall(`/workorder/${id}`, {
      method: 'DELETE'
    });
  }

  async getWorkOrderSummary() {
    return await this.apiCall('/workorder/summary');
  }

  async bulkImportWorkOrders(workOrders) {
    return await this.apiCall('/workorder/bulk-import', {
      method: 'POST',
      body: JSON.stringify({ workOrders })
    });
  }

  // Google Sheet review/import (read-only preview + explicit approve step)
  async getSheetImportPending() {
    return await this.apiCall('/sheetimport/pending');
  }

  async approveSheetImport(rows) {
    return await this.apiCall('/sheetimport/approve', {
      method: 'POST',
      body: JSON.stringify({ rows })
    });
  }

  async getSheetAudit() {
    return await this.apiCall('/sheetimport/audit');
  }

  async generateInvoiceFromWorkOrder(workOrderId, invoiceData) {
    return await this.apiCall(`/workorder/${workOrderId}/generate-invoice`, {
      method: 'POST',
      body: JSON.stringify(invoiceData)
    });
  }

  // Staging Area Management
  async debugWorkOrder(workOrderId) {
    return await this.apiCall(`/staging/debug-workorder/${workOrderId}`);
  }

  async finalizeWorkOrder(workOrderId, stagingData) {
    return await this.apiCall(`/staging/finalize-workorder/${workOrderId}`, {
      method: 'POST',
      body: JSON.stringify(stagingData)
    });
  }

  async getStagingArea() {
    return await this.apiCall('/staging/area');
  }

  async batchProcessStaged(processRequest) {
    return await this.apiCall('/staging/batch-process', {
      method: 'POST',
      body: JSON.stringify(processRequest)
    });
  }

  async retryFailedStaged() {
    return await this.apiCall('/staging/retry-failed', {
      method: 'POST'
    });
  }

  async deleteStagedInvoice(id) {
    return await this.apiCall(`/staging/invoice/${id}`, {
      method: 'DELETE'
    });
  }

  async deleteStagedBill(id) {
    return await this.apiCall(`/staging/bill/${id}`, {
      method: 'DELETE'
    });
  }

  async clearWorkOrderStaging(workOrderId) {
    return await this.apiCall(`/staging/clear-workorder/${workOrderId}`, {
      method: 'DELETE'
    });
  }

  async clearAllStaged(resetWorkOrders = false) {
    const url = resetWorkOrders 
      ? '/staging/clear-all-staged?resetWorkOrders=true'
      : '/staging/clear-all-staged';
    return await this.apiCall(url, {
      method: 'DELETE'
    });
  }


  // Contact Enhancement Management
  async getEnhancedContacts() {
    return await this.apiCall('/contactenhancement/enhanced-contacts');
  }

  async createContactEnhancement(enhancementData) {
    return await this.apiCall('/contactenhancement', {
      method: 'POST',
      body: JSON.stringify(enhancementData)
    });
  }

  async getContactEnhancement(xeroContactId) {
    return await this.apiCall(`/contactenhancement/${xeroContactId}`);
  }

  async updateContactEnhancement(xeroContactId, enhancementData) {
    return await this.apiCall(`/contactenhancement/${xeroContactId}`, {
      method: 'PUT',
      body: JSON.stringify(enhancementData)
    });
  }

  async updateContactLastUsed(xeroContactId) {
    return await this.apiCall(`/contactenhancement/${xeroContactId}/update-last-used`, {
      method: 'POST'
    });
  }

  // Client Approval Management
  async importClientApprovals(approvals) {
    console.log('📤 Sending import request with', approvals.length, 'approvals');
    console.log('📋 First 3 work order numbers being imported:', 
      approvals.slice(0, 3).map(a => a.workOrderNumber));
    
    return await this.apiCall('/clientapproval/import', {
      method: 'POST',
      body: JSON.stringify({ approvals })
    });
  }

  async getReadyForProcessing() {
    return await this.apiCall('/clientapproval/ready-for-processing');
  }

  async debugWorkOrders() {
    return await this.apiCall('/clientapproval/debug-workorders');
  }

  async getTeamLookup() {
    return await this.apiCall('/clientapproval/team-lookup');
  }

  // Team Dashboard
  async getTeamDashboardData(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    return await this.apiCall(`/dashboard/team-performance${params ? '?' + params : ''}`);
  }

  async getContractorPayments() {
    return await this.apiCall('/dashboard/contractor-payments');
  }
}

// Export singleton instance
export default new ApiService();
